// frontend/src/constants/index.js
/**
 * General application constants.
 */

// Application version — injected from package.json at build time via the Vite
// `define` for __APP_VERSION__ (see vite.config.js), so the footer/changelog
// version stays in sync with package.json automatically. The fallback only
// applies if the define is somehow absent.
/* global __APP_VERSION__ */
export const APP_VERSION = typeof __APP_VERSION__ !== 'undefined' ? __APP_VERSION__ : '0.0.0';

// User roles
// Freischaltung von Funktionen, die schon gebaut, aber noch nicht für den
// Betrieb freigegeben sind. Auf true setzen blendet den Einstieg wieder ein.
export const FEATURES = {
  // E-Mail-Postfach fürs Büro: Seite und Route (/emails) existieren, der
  // Menüpunkt bleibt aber verborgen, bis Mail freigeschaltet ist.
  EMAIL: false,
};

export const ROLES = {
  ADMIN: 'admin',
  OFFICE: 'office',
  STANDARD: 'standard',
  DEV: 'dev'
};

// Appointment statuses
export const APPOINTMENT_STATUS = {
  PLANNED: 'planned',
  ASSIGNED: 'assigned',
  EN_ROUTE: 'en-route',
  IN_PROGRESS: 'in-progress',
  COMPLETED: 'completed',
  CANCELLED: 'cancelled'
};

// Status labels in German
export const APPOINTMENT_STATUS_LABELS = {
  [APPOINTMENT_STATUS.PLANNED]: 'Geplant',
  [APPOINTMENT_STATUS.ASSIGNED]: 'Zugewiesen',
  [APPOINTMENT_STATUS.EN_ROUTE]: 'Unterwegs',
  [APPOINTMENT_STATUS.IN_PROGRESS]: 'In Arbeit',
  [APPOINTMENT_STATUS.COMPLETED]: 'Abgeschlossen',
  [APPOINTMENT_STATUS.CANCELLED]: 'Abgesagt'
};

// Status colors
export const APPOINTMENT_STATUS_COLORS = {
  [APPOINTMENT_STATUS.PLANNED]: '#9e9e9e',
  [APPOINTMENT_STATUS.ASSIGNED]: '#2196f3',
  [APPOINTMENT_STATUS.EN_ROUTE]: '#ff9800',
  [APPOINTMENT_STATUS.IN_PROGRESS]: '#4caf50',
  [APPOINTMENT_STATUS.COMPLETED]: '#66bb6a',
  [APPOINTMENT_STATUS.CANCELLED]: '#f44336'
};

// Appointment visibility levels
export const VISIBILITY = {
  PUBLIC: 'public',
  TEAM: 'team',
  PRIVATE: 'private'
};

// Visibility labels in German
export const VISIBILITY_LABELS = {
  [VISIBILITY.PUBLIC]: 'Öffentlich',
  [VISIBILITY.TEAM]: 'Team',
  [VISIBILITY.PRIVATE]: 'Privat'
};

// Work entry statuses
export const WORK_STATUS = {
  DRAFT: 'draft',
  SUBMITTED: 'submitted',
  APPROVED: 'approved',
  REJECTED: 'rejected'
};

// Work status labels in German
export const WORK_STATUS_LABELS = {
  [WORK_STATUS.DRAFT]: 'Entwurf',
  [WORK_STATUS.SUBMITTED]: 'Eingereicht',
  [WORK_STATUS.APPROVED]: 'Genehmigt',
  [WORK_STATUS.REJECTED]: 'Abgelehnt'
};

// Work item categories
export const WORK_CATEGORIES = {
  APPOINTMENT: 'appointment',
  PREPARATION: 'preparation',
  OFFICE: 'office',
  TRAVEL: 'travel',
  OTHER: 'other'
};

// Work category labels in German
export const WORK_CATEGORY_LABELS = {
  [WORK_CATEGORIES.APPOINTMENT]: 'Termin',
  [WORK_CATEGORIES.PREPARATION]: 'Vorbereitung',
  [WORK_CATEGORIES.OFFICE]: 'Büro',
  [WORK_CATEGORIES.TRAVEL]: 'Reise',
  [WORK_CATEGORIES.OTHER]: 'Sonstiges'
};

// Invoice statuses
export const INVOICE_STATUS = {
  DRAFT: 'draft',
  FINALIZED: 'finalized',
  SENT: 'sent',
  PAID: 'paid',
  OVERDUE: 'overdue',
  CANCELLED: 'cancelled'
};

// Invoice status labels in German (D10: Rechnung storniert = "Storniert")
export const INVOICE_STATUS_LABELS = {
  [INVOICE_STATUS.DRAFT]: 'Entwurf',
  [INVOICE_STATUS.FINALIZED]: 'Finalisiert',
  [INVOICE_STATUS.SENT]: 'Versendet',
  [INVOICE_STATUS.PAID]: 'Bezahlt',
  [INVOICE_STATUS.OVERDUE]: 'Überfällig',
  [INVOICE_STATUS.CANCELLED]: 'Storniert'
};

// Recurrence frequencies
export const RECURRENCE_FREQUENCY = {
  DAILY: 'daily',
  WEEKLY: 'weekly',
  MONTHLY: 'monthly',
  YEARLY: 'yearly'
};

// Recurrence frequency labels in German
export const RECURRENCE_FREQUENCY_LABELS = {
  [RECURRENCE_FREQUENCY.DAILY]: 'Täglich',
  [RECURRENCE_FREQUENCY.WEEKLY]: 'Wöchentlich',
  [RECURRENCE_FREQUENCY.MONTHLY]: 'Monatlich',
  [RECURRENCE_FREQUENCY.YEARLY]: 'Jährlich'
};

// Recurrence scope
export const RECURRENCE_SCOPE = {
  THIS: 'this',
  FUTURE: 'future',
  ALL: 'all'
};

// Date formats
export const DATE_FORMAT = 'dd.MM.yyyy';
export const TIME_FORMAT = 'HH:mm';
export const DATETIME_FORMAT = 'dd.MM.yyyy HH:mm';

// Pagination
export const DEFAULT_PAGE_SIZE = 50;
export const PAGE_SIZE_OPTIONS = [25, 50, 100, 200];

// File upload limits
export const MAX_FILE_SIZE_MB = 10;
export const MAX_ATTACHMENTS = 10;
export const ALLOWED_FILE_EXTENSIONS = [
  '.pdf', '.jpg', '.jpeg', '.png',
  '.doc', '.docx', '.txt',
  '.xlsx', '.xls'
];

// Weekdays (0 = Sunday in JavaScript)
export const WEEKDAYS_DE = {
  0: 'Sonntag',
  1: 'Montag',
  2: 'Dienstag',
  3: 'Mittwoch',
  4: 'Donnerstag',
  5: 'Freitag',
  6: 'Samstag'
};

// Default work hours
export const DEFAULT_WORK_HOURS = {
  START: '09:00',
  END: '17:00',
  WEEKLY_HOURS: 38.0
};

// Notification types
export const NOTIFICATION_TYPE = {
  APPOINTMENT_ASSIGNED: 'appointment_assigned',
  APPOINTMENT_UPDATED: 'appointment_updated',
  REPORT_SUBMITTED: 'report_submitted',
  WORK_ENTRY_APPROVED: 'work_entry_approved',
  WORK_ENTRY_REJECTED: 'work_entry_rejected',
  INVOICE_CREATED: 'invoice_created',
  VERSION_UPDATE: 'version_update'
};

// Local storage keys
export const STORAGE_KEYS = {
  AUTH_TOKEN: 'auth_token',
  USER_PREFERENCES: 'user_preferences',
  THEME: 'theme'
};

// ---------------------------------------------------------------------------
// Canonical presentation maps (D10: labels/colors central in constants).
//
// These capture the CURRENT superset that is duplicated across components
// (EventView, AppointmentsList, CalendarToolbar, InvoicesList, InvoiceEditor,
// WorkHoursPage, WorkHoursManager) so that later swaps to shared imports are
// drop-in. Existing exports above are kept unchanged for backward compat.
// ---------------------------------------------------------------------------

import AccessTimeIcon from '@mui/icons-material/AccessTime';
import BuildIcon from '@mui/icons-material/Build';
import BusinessIcon from '@mui/icons-material/Business';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import WorkIcon from '@mui/icons-material/Work';

// Appointment status → MUI Chip `color` name (as used in EventView).
export const APPOINTMENT_STATUS_CHIP_COLORS = {
  [APPOINTMENT_STATUS.PLANNED]: 'primary',
  [APPOINTMENT_STATUS.ASSIGNED]: 'info',
  [APPOINTMENT_STATUS.EN_ROUTE]: 'warning',
  [APPOINTMENT_STATUS.IN_PROGRESS]: 'secondary',
  [APPOINTMENT_STATUS.COMPLETED]: 'success',
  [APPOINTMENT_STATUS.CANCELLED]: 'error'
};

// Invoice status → MUI Chip `color` name (as used in InvoicesList/InvoiceEditor).
export const INVOICE_STATUS_CHIP_COLORS = {
  [INVOICE_STATUS.DRAFT]: 'default',
  [INVOICE_STATUS.FINALIZED]: 'info',
  [INVOICE_STATUS.SENT]: 'primary',
  [INVOICE_STATUS.PAID]: 'success',
  [INVOICE_STATUS.OVERDUE]: 'error',
  [INVOICE_STATUS.CANCELLED]: 'warning'
};

// Work-entry status → MUI Chip `color` name (as used in WorkHoursPage/Manager).
export const WORK_STATUS_CHIP_COLORS = {
  [WORK_STATUS.DRAFT]: 'default',
  [WORK_STATUS.SUBMITTED]: 'warning',
  [WORK_STATUS.APPROVED]: 'success',
  [WORK_STATUS.REJECTED]: 'error'
};

// Work-category presentation metadata: label + icon component + color.
//
// `icon` is the MUI icon COMPONENT (render as `<meta.icon />`), not a JSX
// element, so this module needs no JSX transform. Labels match what the UI
// currently renders ('Büroarbeit'/'Fahrt'), which is the drop-in superset for
// the two identical WorkHours copies (distinct from the terser
// WORK_CATEGORY_LABELS enum above, kept for backward compat).
export const WORK_CATEGORY_META = {
  [WORK_CATEGORIES.APPOINTMENT]: { label: 'Termin', icon: AccessTimeIcon, color: '#2196f3' },
  [WORK_CATEGORIES.PREPARATION]: { label: 'Vorbereitung', icon: BuildIcon, color: '#ff9800' },
  [WORK_CATEGORIES.OFFICE]: { label: 'Büroarbeit', icon: BusinessIcon, color: '#4caf50' },
  [WORK_CATEGORIES.TRAVEL]: { label: 'Fahrt', icon: DirectionsCarIcon, color: '#9c27b0' },
  [WORK_CATEGORIES.OTHER]: { label: 'Sonstiges', icon: WorkIcon, color: '#607d8b' }
};

// Ordered array form (matches the `WORK_CATEGORIES` array copies in
// WorkHoursPage/WorkHoursManager: [{ value, label, icon, color }]).
// `icon` here stays a component; callers render `<Icon />`.
export const WORK_CATEGORY_OPTIONS = Object.entries(WORK_CATEGORY_META).map(
  ([value, meta]) => ({ value, ...meta })
);
